import { ColorVariant, IconName } from "@mitratech/north-star";

import { CaseStatus, LeavePeriod } from "./loa";
import statusChipStyles from "./statusChipColors.module.scss";

const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  [CaseStatus.Open]: "Open",
  [CaseStatus.Closed]: "Closed",
  [CaseStatus.Cancelled]: "Cancelled",
  [CaseStatus.Pending]: "Pending",
  [CaseStatus.Submitted]: "Submitted",
  [CaseStatus.Accepted]: "Accepted",
  [CaseStatus.NotRequired]: "Not Required",
  [CaseStatus.Rejected]: "Rejected",
  [CaseStatus.Approved]: "Approved",
  [CaseStatus.Denied]: "Denied",
};

/**
 * The statuses of cases still being worked: Open, and Approved (whose action
 * items are the work). Case lists and counts that mean "not finished" use it.
 */
export const ACTIVE_CASE_STATUSES: readonly CaseStatus[] = [CaseStatus.Open, CaseStatus.Approved];

export function caseStatusLabel(status: CaseStatus): string {
  return CASE_STATUS_LABELS[status] ?? "Unknown";
}

const CASE_STATUS_COLORS: Partial<Record<CaseStatus, ColorVariant>> = {
  [CaseStatus.Open]: ColorVariant.Primary,
  [CaseStatus.Approved]: ColorVariant.Success,
  [CaseStatus.Closed]: ColorVariant.Secondary,
  // Cancelled and Denied are different events with similar effects (neither
  // takes leave), so they get different colors: amber for a withdrawal, red
  // for a refusal (LOA-1521).
  [CaseStatus.Cancelled]: ColorVariant.Warning,
  [CaseStatus.Denied]: ColorVariant.Error,
};

/** The badge color for a status, shared by the case page, the case list and the status filter. */
export function caseStatusColor(status: CaseStatus): ColorVariant {
  return CASE_STATUS_COLORS[status] ?? ColorVariant.Secondary;
}

const CASE_STATUS_CHIP_CLASSES: Partial<Record<CaseStatus, string>> = {
  [CaseStatus.Open]: statusChipStyles.statusChipOpen,
  [CaseStatus.Approved]: statusChipStyles.statusChipApproved,
  [CaseStatus.Closed]: statusChipStyles.statusChipClosed,
  [CaseStatus.Cancelled]: statusChipStyles.statusChipCancelled,
  [CaseStatus.Denied]: statusChipStyles.statusChipDenied,
};

/**
 * Per-status background/text override class (LOA-1700) for the read-only case page's status
 * chip. `ColorVariant`'s fixed palette doesn't cover these status colors, so this is passed
 * alongside `caseStatusColor` as the chip's `className`.
 */
export function caseStatusChipClassName(status: CaseStatus): string | undefined {
  return CASE_STATUS_CHIP_CLASSES[status];
}

const CASE_STATUS_FILTER_CHIP_CLASSES: Partial<Record<CaseStatus, string>> = {
  [CaseStatus.Open]: statusChipStyles.filterChipOpen,
  [CaseStatus.Approved]: statusChipStyles.filterChipApproved,
  [CaseStatus.Denied]: statusChipStyles.filterChipDenied,
  [CaseStatus.Closed]: statusChipStyles.filterChipClosed,
  [CaseStatus.Cancelled]: statusChipStyles.filterChipCancelled,
};

/**
 * Per-status override class (LOA-1700) for the cases sidebar's active-filter chip — distinct
 * from `caseStatusChipClassName` by design, so this pill doesn't read as the same control as
 * the case page's status chip.
 */
export function caseStatusFilterChipClassName(status: CaseStatus): string | undefined {
  return CASE_STATUS_FILTER_CHIP_CLASSES[status];
}

const CASE_STATUS_ICONS: Partial<Record<CaseStatus, IconName>> = {
  [CaseStatus.Open]: IconName.FolderOpen,
  [CaseStatus.Approved]: IconName.CheckCircle,
  [CaseStatus.Closed]: IconName.Lock,
  [CaseStatus.Cancelled]: IconName.RemoveCircleOutline,
  [CaseStatus.Denied]: IconName.Cancel,
};

/**
 * The glyph a status chip carries before its text, so color is never the only thing that tells
 * Denied from Cancelled (LOA-1521 AC7). Each is a distinct shape that takes the chip's text
 * color, unlike the sidebar filter's status dots, which are the status color on its own.
 */
export function caseStatusIcon(status: CaseStatus): IconName | undefined {
  return CASE_STATUS_ICONS[status];
}

const LEAVE_PERIOD_LABELS: Record<LeavePeriod, string> = {
  [LeavePeriod.Hours]: "hours",
  [LeavePeriod.Days]: "days",
  [LeavePeriod.Weeks]: "weeks",
  [LeavePeriod.Months]: "months",
  [LeavePeriod.Years]: "years",
};

export function leavePeriodLabel(period: LeavePeriod): string {
  return LEAVE_PERIOD_LABELS[period] ?? "";
}

/**
 * Mirrors the backend `CaseStatus.allowed_transitions` (apps/leave/enums.py).
 * An `Open` case is approved, denied or cancelled (it cannot close without
 * being approved); an `Approved` case may go back to `Open` (a revision, which
 * clears the approval), or be closed or cancelled; Denied, Cancelled and
 * Closed cases can only be reopened (LOA-1520).
 */
export function allowedStatusTransitions(current: CaseStatus): CaseStatus[] {
  switch (current) {
    case CaseStatus.Open:
      return [CaseStatus.Approved, CaseStatus.Denied, CaseStatus.Cancelled];
    case CaseStatus.Approved:
      return [CaseStatus.Open, CaseStatus.Closed, CaseStatus.Cancelled];
    case CaseStatus.Closed:
    case CaseStatus.Cancelled:
    case CaseStatus.Denied:
      return [CaseStatus.Open];
    default:
      return [];
  }
}

/**
 * Check if a case status is terminal (non-Open).
 * Terminal cases are read-only: no edits allowed (LOA-1085). An approved
 * case counts: its details are locked, and it is revised from the Quick chat.
 */
export function isTerminalStatus(status: CaseStatus): boolean {
  return status !== CaseStatus.Open;
}
